/**
 * storageUsage.ts — Monitor de uso del almacenamiento local, por módulo.
 *
 * QUÉ HACE:
 *  - Recorre las claves de localStorage y calcula el espacio real usado en
 *    bytes (UTF-16: (longitud de clave + longitud de valor) × 2), agrupado
 *    por módulo del negocio (Caja, Inventario, Auditoría, Personal, …).
 *  - Establece una capacidad práctica de referencia para localStorage
 *    (~5 MB típico en WebView Android/Chromium) y clasifica el nivel de
 *    riesgo: OK <70% · Aviso ≥70% · Alerta ≥85% · Crítico ≥95%.
 *  - Provee `getOriginStorageEstimate()` (navigator.storage.estimate) para
 *    mostrar la cuota total del origen (incluye el espejo IndexedDB).
 *  - Expone el nombre de evento `STORAGE_SETTINGS_OPEN_EVENT` para que la UI
 *    pueda abrir la sección "Almacenamiento y respaldo" con un solo clic.
 *
 * QUÉ EXPORTA:
 *  - getStorageUsageReport(), getOriginStorageEstimate()
 *  - StorageUsageReport / StorageModuleUsage / StorageUsageLevel (tipos)
 *  - Umbrales y capacidad de referencia.
 *
 * QUÉ NO DEBE HACER:
 *  - No modificar ningún dato (solo lectura de claves).
 *  - No importar React, componentes ni UI.
 */
import { idbGetAll } from "./idbMirrorBackend";

// ── Constantes de referencia ─────────────────────────────────────────────────

/**
 * Capacidad práctica de referencia de localStorage en WebView/Chromium.
 * Es conservador: algunos motores dan 10 MB. Se usa para el porcentaje visual.
 */
export const LOCAL_STORAGE_REF_CAP_BYTES = 5 * 1024 * 1024;

export const STORAGE_WARNING_PCT = 70;
export const STORAGE_ALERT_PCT = 85;
export const STORAGE_CRITICAL_PCT = 95;

/** Evento global para abrir Ajustes → Almacenamiento desde banners/toasts. */
export const STORAGE_SETTINGS_OPEN_EVENT = "altenot:open-settings";

// ── Tipos ────────────────────────────────────────────────────────────────────

export type StorageUsageLevel = "ok" | "warning" | "alert" | "critical";

export interface StorageModuleUsage {
  /** Nombre legible del módulo (Caja, Inventario, …). */
  module: string;
  /** Bytes usados por el módulo. */
  bytes: number;
  /** Número de claves que componen el módulo. */
  keys: number;
}

export interface StorageUsageReport {
  /** Bytes totales usados en localStorage. */
  totalBytes: number;
  /** Capacidad de referencia (5 MB). */
  capBytes: number;
  /** Porcentaje usado respecto a la capacidad de referencia. */
  percentUsed: number;
  /** Nivel de riesgo derivado del porcentaje. */
  level: StorageUsageLevel;
  /** Desglose por módulo, ordenado de mayor a menor uso. */
  modules: StorageModuleUsage[];
  /** Total de claves en localStorage. */
  keyCount: number;
  /** Bytes del espejo IndexedDB (cálculo best-effort). */
  idbMirrorBytes: number;
}

export interface OriginStorageEstimate {
  usageBytes: number;
  quotaBytes: number;
  percentUsed: number;
}

// ── Agrupación por módulo ────────────────────────────────────────────────────

interface ModuleMatcher {
  module: string;
  matches: (key: string) => boolean;
}

/**
 * Orden importa: las claves con nombre más específico se evalúan primero
 * (ej. documentos base64 grandes antes que su prefijo de módulo).
 */
const MODULE_MATCHERS: ModuleMatcher[] = [
  {
    module: "Documentos y adjuntos",
    matches: (k) =>
      k === "postulaciones_list" ||
      k === "vacantes_list" ||
      k === "business_compliance_documents" ||
      k === "payroll_employee_documents_v1" ||
      k === "employee_biometrics_v1",
  },
  {
    module: "Caja",
    matches: (k) => k.startsWith("caja_") || k.startsWith("creditos_"),
  },
  { module: "Inventario", matches: (k) => k.startsWith("inventory_") },
  { module: "Auditoría", matches: (k) => k.startsWith("auditoria_") },
  { module: "Personal", matches: (k) => k.startsWith("personal_") },
  {
    module: "Nómina",
    matches: (k) => k.startsWith("payroll_") || k.startsWith("nomina_"),
  },
  { module: "Fidelización", matches: (k) => k.startsWith("fidelizacion_") },
  { module: "Tienda online", matches: (k) => k.startsWith("tienda_") },
  {
    module: "Higiene",
    matches: (k) =>
      k.startsWith("higiene_") || k.startsWith("altenot_higiene_"),
  },
  { module: "Calificaciones", matches: (k) => k.startsWith("calificaciones_") },
  {
    module: "Configuración",
    matches: (k) => k.startsWith("businessConfig") || k.startsWith("business_"),
  },
  { module: "Otros / Sistema", matches: () => true },
];

function moduleForKey(key: string): string {
  for (const m of MODULE_MATCHERS) {
    if (m.matches(key)) return m.module;
  }
  return "Otros / Sistema";
}

// ── Cálculo de uso ───────────────────────────────────────────────────────────

/** Convierte un nivel de porcentaje a nivel de riesgo. */
export function getLevelForPercent(percentUsed: number): StorageUsageLevel {
  if (percentUsed >= STORAGE_CRITICAL_PCT) return "critical";
  if (percentUsed >= STORAGE_ALERT_PCT) return "alert";
  if (percentUsed >= STORAGE_WARNING_PCT) return "warning";
  return "ok";
}

/** Lee localStorage de forma segura (puede lanzar en entornos restringidos). */
function readAllLocalStorageEntries(): { key: string; value: string }[] {
  const entries: { key: string; value: string }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key === null) continue;
    const value = localStorage.getItem(key);
    if (value === null) continue;
    entries.push({ key, value });
  }
  return entries;
}

/** Calcula bytes UTF-16 de una clave + su valor. */
function keyValueBytes(key: string, value: string): number {
  return (key.length + value.length) * 2;
}

/**
 * Genera el reporte de uso de localStorage agrupado por módulo.
 * Síncrono: no requiere permisos ni esperas.
 */
export function getStorageUsageReport(): StorageUsageReport {
  const perModule = new Map<string, { bytes: number; keys: number }>();

  const entries = readAllLocalStorageEntries();
  let totalBytes = 0;

  for (const { key, value } of entries) {
    const bytes = keyValueBytes(key, value);
    totalBytes += bytes;
    const module = moduleForKey(key);
    const acc = perModule.get(module) ?? { bytes: 0, keys: 0 };
    acc.bytes += bytes;
    acc.keys += 1;
    perModule.set(module, acc);
  }

  const modules = [...perModule.entries()]
    .map(([module, acc]) => ({ module, bytes: acc.bytes, keys: acc.keys }))
    .sort((a, b) => b.bytes - a.bytes);

  const percentUsed =
    LOCAL_STORAGE_REF_CAP_BYTES > 0
      ? Math.min(100, (totalBytes / LOCAL_STORAGE_REF_CAP_BYTES) * 100)
      : 0;

  return {
    totalBytes,
    capBytes: LOCAL_STORAGE_REF_CAP_BYTES,
    percentUsed: Math.round(percentUsed * 10) / 10,
    level: getLevelForPercent(percentUsed),
    modules,
    keyCount: entries.length,
    idbMirrorBytes: 0,
  };
}

/** Calcula el tamaño aproximado del espejo IndexedDB (best-effort). */
export async function getMirrorSizeBytes(): Promise<number> {
  try {
    const entries = await idbGetAll();
    let bytes = 0;
    for (const [key, value] of entries) {
      bytes += (key.length + value.length) * 2;
    }
    return bytes;
  } catch {
    return 0;
  }
}

/**
 * Consulta la cuota total del origen vía Storage API (incluye IndexedDB).
 * Devuelve null si el navegador no la soporta.
 */
export async function getOriginStorageEstimate(): Promise<OriginStorageEstimate | null> {
  try {
    if (
      !navigator.storage ||
      typeof navigator.storage.estimate !== "function"
    ) {
      return null;
    }
    const est = await navigator.storage.estimate();
    const usageBytes = est.usage ?? 0;
    const quotaBytes = est.quota ?? 0;
    return {
      usageBytes,
      quotaBytes,
      percentUsed: quotaBytes > 0 ? (usageBytes / quotaBytes) * 100 : 0,
    };
  } catch {
    return null;
  }
}

/** Formatea bytes a una unidad legible (B / KB / MB). */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const value = bytes / 1024 ** i;
  return `${value >= 100 ? Math.round(value) : Math.round(value * 10) / 10} ${units[i]}`;
}
